import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  TextField, Switch, FormControlLabel, Box, Tooltip, IconButton,
  Typography, 
  InputAdornment, Grid, 
  Card, CardContent, CardHeader
} from '@mui/material';
import ActionSettingsLayout from '../ActionSettingsHeader';
import TagEditor from '../Widgets/TagEditor';
import TagsDropdown from '../TagsDropdown';

import { useWebSocket } from '../WebSocketContext';
import { isActionEnabled, buildGetExportSettingsPayload } from '../../Utils/actionSettingsUtils';

const ActionsOIIQVerifyNurse = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  // Prepopulated values
  const DEFAULT_STARTING_URL = 'https://www.oiiq.org/bottin-des-membres';
  const DEFAULT_TAG = 'oiiq_nurse_verification';
  const DEFAULT_INSTRUCTIONS = 'Search for the nurse using their license number {nurse_licence} or registration number in the OIIQ directory. If the license number is not available, use the nurse name. Verify if the nurse is registered and active. Extract the following information: registration number, specialties, practice location, registration status, and any disciplinary actions. Return the results in a structured format.';
  
  const [instructions, setInstructions] = useState(DEFAULT_INSTRUCTIONS);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const initialSettings = useRef({});
  const { ws, subscribeToMessages } = useWebSocket();
  const [forceRunLoading, setForceRunLoading] = useState(false);
  const [forceRunResult, setForceRunResult] = useState(null);
  const [popUpOpen, setPopUpOpen] = useState(false);
  const [id, setId] = useState(settingId || 0);
  const [tag, setTag] = useState(DEFAULT_TAG);
  const [startingUrl, setStartingUrl] = useState(DEFAULT_STARTING_URL);
  const [maxIterations, setMaxIterations] = useState(10);
  const [headless, setHeadless] = useState(true);
  const [conditions, setConditions] = useState([]);

  // Tag Editor states
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuType, setMenuType] = useState('');
  
  const tagRef = useRef(null);
  const instructionsRef = useRef(null);

  useEffect(() => {
    const isTemporaryId = typeof settingId === 'string' && settingId.startsWith('temp_');
    
    if (ws && settingId && !isTemporaryId) {
      ws.send(JSON.stringify(buildGetExportSettingsPayload(settingId, caseid)));
    } else if (isTemporaryId) {
      setId(settingId);
      setIsEnabled(false);
      setInstructions(DEFAULT_INSTRUCTIONS);
      setTag(DEFAULT_TAG);
      setStartingUrl(DEFAULT_STARTING_URL);
      setHeadless(true);
      setConditions([]);
      
      initialSettings.current = {
        id: settingId,
        enable: false,
        tag: DEFAULT_TAG,
        startingUrl: DEFAULT_STARTING_URL,
        headless: true,
        instructions: DEFAULT_INSTRUCTIONS,
        useConditions: true,
        conditions: [],
        data: { name: 'OIIQ Verify Nurse' }
      };
      setIsSaveDisabled(false);
    }
  }, [ws, caseid, settingId]);

  useEffect(() => {
    const unsubscribe = subscribeToMessages((parsedMessage) => {
      try {
        if (parsedMessage.command === 'getExportSettings' && parsedMessage.data.type === 'oiiqverifynurse') {
          console.log('Received export settings:', parsedMessage.data);
          const { id: newId, enable, customData } = parsedMessage.data;
          setId(newId);
          setIsEnabled(isActionEnabled(enable));
          
          if (customData.tag !== undefined) setTag(customData.tag);
          if (customData.startingUrl !== undefined) setStartingUrl(customData.startingUrl);
          if (customData.maxIterations !== undefined) setMaxIterations(customData.maxIterations);
          if (customData.headless !== undefined) setHeadless(customData.headless);
          if (customData.instructions !== undefined) {
            setInstructions(customData.instructions);
          }
          if (customData?.conditions !== undefined) setConditions(customData.conditions);

          initialSettings.current = {
            id: newId,
            enable: isActionEnabled(enable),
            tag: customData.tag || DEFAULT_TAG,
            startingUrl: customData.startingUrl || DEFAULT_STARTING_URL,
            instructions: customData.instructions || DEFAULT_INSTRUCTIONS,
            maxIterations: customData.maxIterations || 10,
            headless: customData.headless !== undefined ? customData.headless : true,
            useConditions: customData?.useConditions || false,
            conditions: customData?.conditions || []
          };
          setIsSaveDisabled(true);
        } else if (parsedMessage.command === 'saveActionSettings') {
          if (parsedMessage.data && parsedMessage.data.id && parsedMessage.data.type === 'oiiqverifynurse') {
            const savedId = parsedMessage.data.id;
            const savedCustomData = parsedMessage.data.customData;
            
            setId(savedId);
            initialSettings.current = {
              id: savedId,
              enable: isEnabled,
              tag,
              startingUrl,
              instructions: instructions,
              maxIterations,
              headless,
              useConditions: true,
              conditions: [...conditions],
              data: savedCustomData || initialSettings.current.data || {}
            };
            
            if (onSaveStatusChange) { 
              onSaveStatusChange(savedId, false, typeof id === 'string' && id.startsWith('temp_') ? id : null);
            }
            
            setIsSaveDisabled(true);
          }
        } else if (parsedMessage.command === 'forceRun') {
          setForceRunLoading(false);
          setForceRunResult(parsedMessage.data);
          setPopUpOpen(true);
        } else if (parsedMessage.command === 'forceRunError') {
          setForceRunLoading(false);
          setForceRunResult({ error: parsedMessage.data });
          setPopUpOpen(true);
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    });
    return unsubscribe;
  }, [subscribeToMessages, instructions, isEnabled, tag, startingUrl, maxIterations, headless, conditions, onSaveStatusChange, id]);

  const updateSaveDisabledState = useCallback(() => {
    if (initialSettings.current && initialSettings.current.id !== undefined) {
      const currentInstructions = instructions;
      const initialConditionsJSON = JSON.stringify(initialSettings.current.conditions || []);
      const currentConditionsJSON = JSON.stringify(conditions);

      const unchanged =
        currentInstructions === initialSettings.current.instructions &&
        isEnabled === initialSettings.current.enable &&
        tag === initialSettings.current.tag &&
        startingUrl === initialSettings.current.startingUrl &&
        maxIterations === initialSettings.current.maxIterations &&
        headless === initialSettings.current.headless &&
        initialSettings.current.useConditions === true &&
        currentConditionsJSON === initialConditionsJSON;

      const hasChanges = !unchanged;
      
      const currentId = (typeof id === 'string' && id.startsWith('temp_')) ? id : (initialSettings.current.id || id);
      if (onSaveStatusChange) {
        onSaveStatusChange(currentId, hasChanges);
      }
      setIsSaveDisabled(unchanged);
    } else {
      console.log('initialSettings.current is not properly set:', initialSettings.current);
    }
  }, [instructions, isEnabled, tag, startingUrl, conditions, id, maxIterations, headless, onSaveStatusChange]);

  useEffect(() => {
    updateSaveDisabledState();
  }, [instructions, isEnabled, tag, startingUrl, maxIterations, headless, conditions, updateSaveDisabledState]);

  const handleConditionsChange = useCallback((newConditions) => {
    setConditions(newConditions);
  }, []);



  const handleSave = () => {
    if (ws) {
      const currentInstructions = instructions;
      const currentId = (typeof id === 'string' && id.startsWith('temp_')) ? id : (initialSettings.current.id || id);
      
      const settingsData = {
        tag,
        startingUrl,
        instructions: currentInstructions,
        maxIterations,
        headless,
        useConditions: true,
        conditions
      };
      
      const saveData = {
        command: 'saveActionSettings',
        id: currentId,
        caseid,
        type: 'oiiqverifynurse',
        enable: isEnabled ? '1' : '0',
        interval: '0',
        data: settingsData
      };
      ws.send(JSON.stringify(saveData));
    }
  };

  const handleForceRun = () => {
    if (ws) {
      setForceRunLoading(true);
      const currentInstructions = instructions;
      const forceRunData = {
        command: 'forceRun',
        caseid,
        export_id: id,
        customData: {
          tag,
          startingUrl,
          instructions: currentInstructions,
          maxIterations,
          headless,
          useConditions: true,
          conditions
        }
      };
      ws.send(JSON.stringify(forceRunData));
    }
  };



  const handleMenuOpen = (event, type) => {
    setAnchorEl(event.currentTarget);
    setMenuType(type);
  };

  const handleMainTagMenuClose = () => {
    setAnchorEl(null);
    setMenuType('');
  };

  const handleTagSelect = (value) => {
    if (menuType === 'tag') {
      tagRef.current?.insertTag(value);
    } else if (menuType === 'instructions') {
      instructionsRef.current?.insertTag(value);
    }
    handleMainTagMenuClose();
  };

  const closePopUp = () => {
    setPopUpOpen(false);
    setForceRunResult(null);
  };

  return (
    <ActionSettingsLayout
      title="OIIQ Nurse Verification"
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      isSaveDisabled={isSaveDisabled}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      forceRunResult={forceRunResult}
      popUpOpen={popUpOpen}
      onCloseStatusWindow={closePopUp}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
      primaryColor="#2e7d32"
    >
        {/* Configuration Section */}
        <Card sx={{
          mb: 2,
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
          border: '1px solid #2e7d3210',
          overflow: 'hidden',
        }}>
          <CardHeader
            title={
              <Typography sx={{
                fontWeight: 700,
                color: '#444',
                fontSize: { xs: '1rem', sm: '1.25rem' },
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1
              }}>
                <Box component="span" sx={{
                  bgcolor: '#2e7d32',
                  color: 'white',
                  borderRadius: '6px',
                  px: { xs: 1, sm: 1.5 },
                  py: { xs: 0.5, sm: 0.7 },
                  mr: { xs: 1, sm: 2 },
                  fontSize: { xs: '0.75rem', sm: '0.85rem' },
                  letterSpacing: '0.5px',
                  fontWeight: 600,
                  boxShadow: '0 3px 6px rgba(46, 125, 50, 0.2)',
                }}>
                  CONFIG
                </Box>
                Nurse Verification Settings
              </Typography>
            }
            sx={{
              borderBottom: '1px solid #f0f8f0',
              backgroundColor: '#fafff9',
              borderTopLeftRadius: '12px',
              borderTopRightRadius: '12px',
              p: { xs: 1.5, sm: 2.5 },
            }}
          />
          <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#555' }}>
                  Tag/Variable Name
                </Typography>
                <TagEditor
                    caseid={caseid}
                  ref={tagRef}
                  value={tag}
                  onChange={setTag}
                  placeholder="The variable name to store the verification result"
                  singleLine
                  tags={tags}
                  onTagClick={(e) => handleMenuOpen(e, 'tag')}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Max Iterations"
                  type="number"
                  value={maxIterations}
                  onChange={(e) => setMaxIterations(parseInt(e.target.value) || 10)}
                  fullWidth
                  helperText="Maximum number of browsing steps"
                  inputProps={{ min: 1, max: 50 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      '&:hover fieldset': {
                        borderColor: '#2e7d32',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#2e7d32',
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={headless}
                      onChange={(e) => setHeadless(e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase': {
                          '&.Mui-checked': {
                            color: '#2e7d32',
                            '& + .MuiSwitch-track': {
                              backgroundColor: '#2e7d32',
                              opacity: 0.8,
                            },
                          },
                        },
                        '& .MuiSwitch-thumb': {
                          boxShadow: '0 2px 4px 0 rgba(0,0,0,0.2)',
                        },
                        '& .MuiSwitch-track': {
                          borderRadius: 16,
                          opacity: 0.3,
                        },
                      }}
                    />
                  }
                  label="Headless Mode"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#555' }}>
                  Instructions
                </Typography>
                <TagEditor
                    caseid={caseid}
                  ref={instructionsRef}
                  value={instructions}
                  onChange={setInstructions}
                  placeholder="Enter verification instructions..."
                  multiline
                  minRows={3}
                  tags={tags}
                  onTagClick={(e) => handleMenuOpen(e, 'instructions')}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Tag Menu */}
        <TagsDropdown
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          setAnchorEl={handleMainTagMenuClose}
          tags={tags}
          handleTagSelect={handleTagSelect}
          menuType={menuType}
        />
    </ActionSettingsLayout>
  );
};

export default ActionsOIIQVerifyNurse;

